import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Circle as CircleIcon,
  MenuOpen as MenuOpenIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'customer' | 'agent' | 'admin';
  avatar?: string;
}

interface SystemStatus {
  database: boolean;
  mcpServers: boolean;
  aiAgent: boolean;
  llmStatus: boolean;
}

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  user: User;
  systemStatus: SystemStatus;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle, user, systemStatus }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      id: 'home',
      label: 'Home',
      icon: <HomeIcon />,
      path: '/dashboard',
      roles: ['customer', 'agent', 'admin'],
    },
    {
      id: 'chat',
      label: 'AI Chat',
      icon: <ChatIcon />,
      path: '/chat',
      roles: ['customer', 'agent', 'admin'],
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      roles: ['agent', 'admin'],
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: <AnalyticsIcon />,
      path: '/analytics',
      roles: ['agent', 'admin'],
    },
    {
      id: 'admin',
      label: 'Admin Panel',
      icon: <AdminIcon />,
      path: '/admin',
      roles: ['admin'],
    },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user.role)
  );

  const getStatusColor = (status: boolean) => {
    return status ? '#95c100' : '#e74c3c';
  };

  const getStatusText = (status: boolean) => {
    return status ? 'Connected' : 'Disconnected';
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return '#1e212a';
      case 'agent':
        return '#95c100';
      case 'customer':
        return '#1fb8cc';
      default:
        return '#757575';
    }
  };

  const drawerWidth = open ? 280 : 80;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#c9cace',
          borderRight: '1px solid #65676e',
          position: 'fixed',
          height: '100%',
          top: 0,
          left: 0,
          zIndex: 1200,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: 64,
        }}
      >
        {open && (
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1fb8cc' }}>
            Telecom AI
          </Typography>
        )}
        <Tooltip title={open ? 'Collapse sidebar' : 'Expand sidebar'}>
          <Box
            onClick={onToggle}
            sx={{
              cursor: 'pointer',
              p: 0.5,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: '#e3f2fd',
              },
            }}
          >
            {open ? <MenuOpenIcon /> : <MenuIcon />}
          </Box>
        </Tooltip>
      </Box>

      <Divider />

      {/* User Info */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            backgroundColor: '#fff',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <Avatar
            src={user.avatar}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: user.avatar ? 'transparent' : getRoleColor(user.role),
              fontSize: '1rem',
            }}
          >
            {!user.avatar && <PersonIcon />}
          </Avatar>
          {open && (
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.name}
              </Typography>
              <Chip
                label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: getRoleColor(user.role),
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ flexGrow: 1, px: 1 }}>
        {filteredMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          
          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={!open ? item.label : ''} placement="right">
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    minHeight: 48,
                    borderRadius: 2,
                    backgroundColor: isActive ? '#e3f2fd' : 'transparent',
                    color: isActive ? '#1fb8cc' : '#666',
                    '&:hover': {
                      backgroundColor: isActive ? '#e3f2fd' : '#f5f5f5',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: open ? 40 : 'auto',
                      color: isActive ? '#1fb8cc' : '#666',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: isActive ? 600 : 400,
                          fontSize: '0.9rem',
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* System Status */}
      <Box sx={{ p: 2 }}>
        {open && (
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontWeight: 600,
              mb: 1,
              display: 'block',
            }}
          >
            System Status
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Tooltip title={`Database: ${getStatusText(systemStatus.database)}`}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: open ? 'flex-start' : 'center',
              }}
            >
              <CircleIcon
                sx={{
                  fontSize: 12,
                  color: getStatusColor(systemStatus.database),
                }}
              />
              {open && (
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Database
                </Typography>
              )}
            </Box>
          </Tooltip>

          <Tooltip title={`MCP Servers: ${getStatusText(systemStatus.mcpServers)}`}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: open ? 'flex-start' : 'center',
              }}
            >
              <CircleIcon
                sx={{
                  fontSize: 12,
                  color: getStatusColor(systemStatus.mcpServers),
                }}
              />
              {open && (
                <Typography variant="caption" sx={{ color: '#666' }}>
                  MCP Servers
                </Typography>
              )}
            </Box>
          </Tooltip>

          <Tooltip title={`AI Agent: ${getStatusText(systemStatus.aiAgent)}`}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: open ? 'flex-start' : 'center',
              }}
            >
              <CircleIcon
                sx={{
                  fontSize: 12,
                  color: getStatusColor(systemStatus.aiAgent),
                }}
              />
              {open && (
                <Typography variant="caption" sx={{ color: '#666' }}>
                  AI Agent
                </Typography>
              )}
            </Box>
          </Tooltip>

          <Tooltip title={`LLM Status: ${getStatusText(systemStatus.llmStatus)}`}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: open ? 'flex-start' : 'center',
              }}
            >
              <CircleIcon
                sx={{
                  fontSize: 12,
                  color: getStatusColor(systemStatus.llmStatus),
                }}
              />
              {open && (
                <Typography variant="caption" sx={{ color: '#666' }}>
                  LLM Status
                </Typography>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar; 